import React, { useRef, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { Button, Row, Col, Drawer, Input, Form, Space, Typography, DatePicker } from 'antd';
import moment from 'moment';
import { useCurrentCompany } from '../../features/company/companySlice.js';
import { isValidHttpUrl, toTitleCase } from '../../utils/strings.js';
import { useDispatch } from 'react-redux';
import { useUserKey } from '../../features/user/ssoSlice.js';
import style from './Announcements.module.scss';
import { ResponsiveButton } from '../../features/responsive/ResponsiveButton.js';
import { Can, useCan } from '../../features/user/Can.js';
import dayjs from 'dayjs';
import {
  DeleteFilled,
  PlusOutlined,
} from '@ant-design/icons';
import { ANNOUNCEMENT_STATUS_CODE, useSaveAnnouncementMutation } from '../../features/announcement/AnnouncementApi.js';
import { executeSingleAction } from '../../utils/ApiResponseHandler.js';
const { RangePicker } = DatePicker;
const getYearMonth = date => date.year() * 12 + date.month();

const { Title, Text } = Typography;
const { TextArea } = Input;
const MAX_BULLET = 4

const AnnouncementDetailModal = ({ announcementDetail, isModalOpen, onClose }) => {
  const { i18n, t } = useTranslation();
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [form] = Form.useForm();
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const [bulletFields, setBulletFields] = useState({});
  const submitAction = useRef(null);
  const [saveAnnouncement] = useSaveAnnouncementMutation();

  const addButton = () => {
    for (let i = 1; i <= MAX_BULLET; i++) {
      const newFieldName = 'bullet' + i;
      const allFieldValues = form.getFieldsValue();
      const bulletFieldValues = Object.fromEntries(
        Object.entries(allFieldValues).filter(([key]) => key.startsWith('bullet'))
      );
      if (bulletFieldValues[newFieldName] === undefined) {
        form.setFieldsValue({ ...bulletFieldValues, ...{ [newFieldName]: '' } });
        setBulletFields({ ...bulletFieldValues, ...{ [newFieldName]: '' } });
        break;
      }
    }
  };

  const removeField = fieldName => {
    const allFieldValues = form.getFieldsValue();
    const bulletFieldValues = Object.fromEntries(
      Object.entries(allFieldValues).filter(([key]) => key.startsWith('bullet'))
    );
    const copyBulletField = { ...bulletFieldValues };
    delete copyBulletField[fieldName];
    setBulletFields(copyBulletField);
    form.setFieldsValue(copyBulletField);
  };

  useEffect(() => {
    const messageObj = announcementDetail?.message ? JSON.parse(announcementDetail.message) : {};

    const startDate = announcementDetail?.startAt ? moment.parseZone(announcementDetail.startAt) : null;
    const endDate = announcementDetail?.endAt ? moment.parseZone(announcementDetail.endAt) : null;
    let formData = {
      title: announcementDetail?.title,
      intro: messageObj?.intro,
      footer: messageObj?.footer,
      startDate,
      endDate,
      dateRange: startDate && endDate
        ? [dayjs(startDate.format('YYYY-MM-DD')), dayjs(endDate.format('YYYY-MM-DD'))]
        : undefined
    };
    messageObj.bullets?.forEach((bullet, index) => {
      formData['bullet' + (index + 1)] = bullet;
    });

    const copyBulletField = Object.fromEntries(
      Object.entries(formData).filter(([key]) => key.startsWith('bullet'))
    );

    setBulletFields(copyBulletField); 
    form.resetFields();
    form.setFieldsValue(formData);
  }, [form, announcementDetail]);

  const handleFinish = values => {
    const action = submitAction.current || 'draft';
    submitAction.current = null;

    const submittedValues = {
      title: values.title,
      message: JSON.stringify({
        intro: values.intro,
        footer: values.footer,
        bullets: Object.entries(values)
          .filter(([fieldName]) => fieldName.startsWith('bullet'))
          .map(([, bullet]) => bullet)
      }),
      origin: "vlt",
      isActive: action === 'publish',
      endAt: values.endDate ? values.endDate.format('YYYY-MM-DD') : null,
      startAt: values.startDate ? values.startDate.format('YYYY-MM-DD') : null,
      identifier: "Announcement" + announcementDetail?.id,
      loadingMessage: t("Common.Saving"),
      successMessage: t("Announcement.AnnouncementSaved")
    };

    if (announcementDetail?.id) {
      submittedValues.id = announcementDetail.id;
    }

    setIsSubmitting(true);

    executeSingleAction(submittedValues, saveAnnouncement, dispatch).then(() => {
      setIsSubmitting(false);
      if (onClose) {
        onClose();
      }
    }).catch(() => {
      setIsSubmitting(false);
    })
  };

  const submitForm = action => {
    submitAction.current = action;
    form.submit();
  };

  const disabled7DaysDate = (current, { from, type }) => {
    if (from) {
      const minDate = from.add(-6, 'days');
      const maxDate = from.add(6, 'days');
      switch (type) {
        case 'year':
          return current.year() < minDate.year() || current.year() > maxDate.year();
        case 'month':
          return (
            getYearMonth(current) < getYearMonth(minDate) ||
            getYearMonth(current) > getYearMonth(maxDate)
          );
        default:
          return Math.abs(current.diff(from, 'days')) >= 7;
      }
    }
    return false;
  };

  return (
    <Drawer
      title={!!announcementDetail ? `${t("Announcement.EditAnnouncement")}` : `${t("Announcement.NewAnnouncement")}`}
      placement={isMobile ? "bottom" : "right"}
      height={"90%"}
      width={"35%"}
      footer={
        <Space>
          {(announcementDetail?.isActive === false ||  !announcementDetail)&& (
            <Button
            loading={isSubmitting}
            disabled={isSubmitting || !isDirty}
            htmlType="submit"
            type="primary"
            onClick={() => submitForm('draft')}
          >
            {t('Announcement.SaveDraft')}
          </Button>
          )}
          
          <Button
            loading={isSubmitting}
            disabled={isSubmitting || !isDirty}
            htmlType="submit"
            type="primary"
            onClick={() => submitForm('publish')}
          >
            {t('Announcement.Publish')}
          </Button>
        </Space>
      }
      onClose={onClose}
      open={isModalOpen}
    >
      <Form form={form} layout="vertical" onFinish={handleFinish} onValuesChange={() => { setIsDirty(true) }}>
        <Form.Item name="startDate" hidden>
          <Input />
        </Form.Item>
        <Form.Item name="endDate" hidden>
          <Input />
        </Form.Item>
        <Row className={`${style.fullWidthRow}`}>
          <Col xs={{ span: 24 }}>
            <Form.Item label={t('Announcement.Title')} name="title" rules={[
              { required: true, message: t('ValidationErrors.AnnouncementTitleRequired') }
            ]}>
              <Input showCount placeholder={t('Announcement.Title')} maxLength={80} />
            </Form.Item>
          </Col>
          <Col xs={{ span: 24 }}>
            <Form.Item label={t('Announcement.Intro')} name="intro" rules={[
              { required: true, message: t('ValidationErrors.AnnouncementIntroRequired') }
            ]}>
              <TextArea showCount rows={4} placeholder={t('Announcement.Intro')} maxLength={300} />
            </Form.Item>
          </Col>
          <Col xs={{ span: 24 }}>
            <Space direction="vertical" className={`${style.fullWidthRow}`}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Text>{t('Announcement.Bullets')}</Text>
                <Text>{Object.keys(bulletFields || {}).length + "/" + MAX_BULLET}</Text>
              </div>
              {bulletFields &&
                Object.keys(bulletFields).map((e, index) => {
                  return (
                    <Form.Item
                      key={index}
                      name={e}
                      style={{ margin: 0 }}
                      rules={[
                        { required: true, message: t('ValidationErrors.AnnouncementBulletRequired') }
                      ]}
                    >
                      <Input showCount maxLength={140} placeholder={`${t('Announcement.AddBullet')} ${index + 1}`} suffix={
                        <Button
                          type="text"
                          icon={<DeleteFilled />}
                          onClick={() => removeField(e)}
                        />
                      } />
                    </Form.Item>
                  )
                })}
              {Object.keys(bulletFields || {}).length < MAX_BULLET && (
                <Space>
                  <ResponsiveButton
                    onClick={() => addButton()}
                    title={t("Announcement.AddNew")}
                    icon={<PlusOutlined />} />
                </Space>
              )}
            </Space>
          </Col>
          <Col xs={{ span: 24 }} style={{ marginTop: 16 }}>
            <Form.Item label={t('Announcement.Footer')} name="footer">
              <TextArea rows={4} showCount placeholder={t('Announcement.Footer')} maxLength={300} />
            </Form.Item>
          </Col>
        </Row>
        <Row className={`${style.fullWidthRow}`} gutter={16}>
          <Col xs={{ span: 24 }} lg={{ span: 16 }}>
            <Form.Item name="dateRange" label={t('Announcement.StartDate')}>
              <RangePicker disabledDate={disabled7DaysDate} onChange={dates => {
                console.log(dates)
                form.setFieldsValue({
                  startDate: dates?.[0] || null,
                  endDate: dates?.[1] || null
                });
              }} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Drawer>
  )
}

export default AnnouncementDetailModal;